import * as React from 'react'
import ReactDOMServer from 'react-dom/server'
import {S2AIcon, IconByKey, PerformanceIcon, InfoIcon, ClockIcon, LogoutIcon, FlashIcon} from '@oc/icons'
import {messages} from '../translations'
import {S2aProxy, buildFlashLoginUrl} from '@oc/utils'
import {S2ARedirectComponent} from '@oc/components'
import {OcAppBarState} from '../../Model/OcAppBarState'
import {SnackHandlerInstance as snackHandler} from '@oc/shell-core'
import {Ecosystem} from '@oc/customer-v2'

const ocDocumentationLink =
    'https://krones.sharepoint.com/sites/inside_InternationalOperationsAndServices/Krones.digital/SiteAssets/Forms/AllItems.aspx?id=%2Fsites%2Finside%5FInternationalOperationsAndServices%2FKrones%2Edigital%2FSiteAssets%2FSitePages%2FClassic%2DPortfolio%2FUM%5FOperations%2DConsole%5F2022%2D08%5FEN%2Epdf&parent=%2Fsites%2Finside%5FInternationalOperationsAndServices%2FKrones%2Edigital%2FSiteAssets%2FSitePages%2FClassic%2DPortfolio'

const citrixLink = 'https://krones.cloud.com/Citrix/StoreWeb/#/login'

async function openS2aInNewTab(proxy: S2aProxy, service?: string) {
    const newWindow = window.open('about:blank', '_blank')
    if (newWindow) {
        newWindow.document.body.innerHTML = ReactDOMServer.renderToStaticMarkup(<S2ARedirectComponent />)
        try {
            newWindow.location.replace(await proxy.getS2aLink(service))
        } catch (err: any) {
            const errMsg = err?.response?.data?.message
            snackHandler.logError(messages.getText('errors', 'failedToLoginToS2a', errMsg))

            newWindow.document.body.textContent = errMsg
        }
    }
}

function openFlashInNewTab(accountId?: string) {
    const url = buildFlashLoginUrl(accountId)
    const newWindow = window.open(url, '_blank', 'noopener')
    newWindow?.focus()
}

export enum OcAppBarActionContext {
    global,
    timeSettings,
    s2aLinks,
    logout
}
export interface OcAppBarAction {
    id: string
    ocIcon: (state: OcAppBarState) => JSX.Element
    text: (state: OcAppBarState) => string
    onClick: (state: OcAppBarState) => void
    disabled: (state: OcAppBarState) => boolean
    renderContext: OcAppBarActionContext
}

function getS2aLinkTooltip(state: OcAppBarState, isDisabled: boolean, serviceName: string): string {
    if (state.s2aAccountDeactivated) {
        return messages.getText('appBar', 'accountInactive')
    }

    if (isDisabled) {
        return messages.getText('appBar', 'serviceInactive', serviceName)
    }

    return messages.getText('appBar', 'redirect', serviceName)
}

function getS2aLinkDisabledValue(state: OcAppBarState, serviceId: string): boolean {
    if (!state.s2aAccount) {
        return true
    }

    if (state.s2aAccountDeactivated) {
        return true
    }

    if (!state.s2aAccount.services.find(s => s.serviceId === serviceId)) {
        return true
    }

    return false
}

function isFlashAccount(state: OcAppBarState): boolean {
    return state.s2aAccount?.ecosystem === Ecosystem.Flash
}

function getFlashAccountId(state: OcAppBarState): string | undefined {
    if (!state.hasCustomerContext) {
        return undefined
    }

    return state.s2aAccount?.accountId
}

export const ocAppBarActions: OcAppBarAction[] = [
    {
        id: 'user-profile',
        ocIcon: () => <IconByKey iconKey="personnel" size="32px" />,
        text: () => messages.getText('appBar', 'myProfile'),
        onClick: state => state.toggleEditProfileDialog(true),
        disabled: (state: OcAppBarState) => !state.userInfoLoaded,
        renderContext: OcAppBarActionContext.global
    },
    {
        id: 'documentation',
        ocIcon: () => <InfoIcon size="32px" />,
        text: _ => messages.getText('appBar', 'openDocumentation'),
        disabled: _ => false,
        onClick: _ => window.open(ocDocumentationLink, '_blank'),
        renderContext: OcAppBarActionContext.global
    },
    {
        id: 'citrix-workspace',
        ocIcon: () => <IconByKey iconKey="oc_citrix_receiver" size="32px" />,
        text: _ => messages.getText('appBar', 'citrix'),
        disabled: _ => false,
        onClick: _ => window.open(citrixLink, '_blank'),
        renderContext: OcAppBarActionContext.global
    },
    {
        id: 'hmi-studio',
        ocIcon: () => <IconByKey iconKey="navigationArrow-down" rotate="right" size="32px" />,
        text: _ => messages.getText('appBar', 'hmiStudio'),
        disabled: _ => false,
        onClick: _ => {},
        renderContext: OcAppBarActionContext.global
    },
    {
        id: 's2a',
        ocIcon: state => (isFlashAccount(state) ? <FlashIcon /> : <S2AIcon size="32px" />),
        text: function (state) {
            const labelKey = isFlashAccount(state) ? 'flash' : 's2a'
            return getS2aLinkTooltip(state, this.disabled(state), messages.getText('appBar', labelKey))
        },
        disabled: state => {
            return state.s2aAccount ? state.s2aAccountDeactivated : true
        },
        onClick: async function (state) {
            if (isFlashAccount(state)) {
                openFlashInNewTab(getFlashAccountId(state))
                return
            }
            if (state.s2aProxy) {
                await openS2aInNewTab(state.s2aProxy)
            }
        },
        renderContext: OcAppBarActionContext.s2aLinks
    },
    {
        id: 'maintenance',
        ocIcon: () => <IconByKey iconKey="maintenance" size="32px" />,
        text: function (state) {
            return getS2aLinkTooltip(state, this.disabled(state), messages.getText('appBar', 'maintenance'))
        },
        disabled: function (state) {
            return getS2aLinkDisabledValue(state, this.id)
        },
        onClick: async function (state) {
            if (state.s2aProxy) {
                await openS2aInNewTab(state.s2aProxy, this.id)
            }
        },
        renderContext: OcAppBarActionContext.s2aLinks
    },
    {
        id: 'performance',
        ocIcon: () => <PerformanceIcon size="32px" />,
        text: function (state) {
            return getS2aLinkTooltip(state, this.disabled(state), messages.getText('appBar', 'performance'))
        },
        disabled: function (state) {
            return getS2aLinkDisabledValue(state, this.id)
        },
        onClick: async function (state) {
            if (state.s2aProxy) {
                await openS2aInNewTab(state.s2aProxy, this.id)
            }
        },
        renderContext: OcAppBarActionContext.s2aLinks
    },
    {
        id: 'timeline', // id is "timeline", displayed name is "connect"
        ocIcon: () => <IconByKey iconKey="connect" size="32px" />,
        text: function (state) {
            return getS2aLinkTooltip(state, this.disabled(state), messages.getText('appBar', 'connect'))
        },
        disabled: function (state) {
            return getS2aLinkDisabledValue(state, this.id)
        },
        onClick: async function (state) {
            if (state.s2aProxy) {
                await openS2aInNewTab(state.s2aProxy, this.id)
            }
        },
        renderContext: OcAppBarActionContext.s2aLinks
    },
    {
        id: 'v2-watchdog',
        ocIcon: () => <IconByKey iconKey="watchdog" size="32px" />,
        text: function (state) {
            return getS2aLinkTooltip(state, this.disabled(state), messages.getText('appBar', 'watchdog'))
        },
        disabled: function (state) {
            return getS2aLinkDisabledValue(state, this.id)
        },
        onClick: async function (state) {
            if (state.s2aProxy) {
                await openS2aInNewTab(state.s2aProxy, this.id)
            }
        },
        renderContext: OcAppBarActionContext.s2aLinks
    },
    {
        id: 'task-service',
        ocIcon: () => <IconByKey iconKey="task-service" size="32px" />,
        text: function (state) {
            return getS2aLinkTooltip(state, this.disabled(state), messages.getText('appBar', 'taskService'))
        },
        disabled: function (state) {
            return getS2aLinkDisabledValue(state, this.id)
        },
        onClick: async function (state) {
            if (state.s2aProxy) {
                await openS2aInNewTab(state.s2aProxy, this.id)
            }
        },
        renderContext: OcAppBarActionContext.s2aLinks
    },
    {
        id: 'change-timezone', // this action will be overriden in code because of its complexity
        ocIcon: () => <ClockIcon size="32px" />,
        text: _ => '',
        disabled: _ => false,
        onClick: _ => {},
        renderContext: OcAppBarActionContext.timeSettings
    },
    {
        id: 'logout',
        ocIcon: () => <LogoutIcon size="32px" />,
        text: _ => messages.getText('appBar', 'logout'),
        disabled: _ => false,
        onClick: _ => {}, // logout function injected from useLogout
        renderContext: OcAppBarActionContext.logout
    }
]
